/**
 * Dialog / Modal Component — shadcn/ui pattern
 *
 * Componentes compound baseados em @radix-ui/react-dialog com cn().
 * Mantém wrapper `Modal` para retrocompatibilidade com API existente
 * (isOpen, onClose, title, size, children, footer).
 *
 * @module components/ui
 * @since 2025-12-15
 * @updated 2026-04-06 — Migrado para Radix Dialog + cn() (shadcn/ui)
 *
 * @example
 * // Compound components (shadcn/ui pattern)
 * <Dialog open={open} onOpenChange={setOpen}>
 *   <DialogTrigger asChild><Button>Abrir</Button></DialogTrigger>
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Titulo</DialogTitle>
 *       <DialogDescription>Descricao</DialogDescription>
 *     </DialogHeader>
 *     <p>Conteudo</p>
 *     <DialogFooter>
 *       <Button>Salvar</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 *
 * @example
 * // Wrapper retrocompativel
 * <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title="Titulo">
 *   <p>Conteudo do modal</p>
 * </Modal>
 */

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/theme/ThemeContext';
import { cn } from '@/lib/utils';

// ============================================================
// COMPOUND COMPONENTS (shadcn/ui Dialog)
// ============================================================

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/** Tamanhos disponiveis para o modal */
type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full' | 'screen';

/** Classes de tamanho com responsive prefix (compound DialogContent) */
const dialogSizeClasses: Record<ModalSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  full: 'sm:max-w-6xl',
  screen: 'sm:max-w-[90vw]',
};

/** Classes de tamanho sem prefix (wrapper Modal retrocompativel) */
const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: 'max-w-6xl',
  screen: 'max-w-[90vw]',
};

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Tamanho maximo do dialog @default 'lg' */
  size?: ModalSize;
  /** Esconder o botao X de fechar @default false */
  hideClose?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, size = 'lg', hideClose = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        'sm:rounded-lg',
        dialogSizeClasses[size] || dialogSizeClasses.lg,
        className,
      )}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          <span className="sr-only">Fechar</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// ============================================================
// WRAPPER RETROCOMPATIVEL — Modal
// ============================================================

/**
 * Props do componente Modal (wrapper retrocompativel)
 */
interface ModalProps {
  /** Controla se o modal esta aberto ou fechado */
  isOpen: boolean;
  /** Callback chamado quando o modal deve ser fechado */
  onClose: () => void;
  /** Conteudo do corpo do modal */
  children: React.ReactNode;
  /** Titulo exibido no header do modal */
  title?: string;
  /** Tamanho maximo do modal @default 'md' */
  size?: ModalSize;
  /** Se true, fecha o modal ao clicar no overlay @default true */
  closeOnOverlayClick?: boolean;
  /** Se true, fecha o modal ao pressionar ESC @default true */
  closeOnEsc?: boolean;
  /** Se true, exibe o botao X de fechar no header @default true */
  showCloseButton?: boolean;
  /** Conteudo do footer do modal (geralmente botoes de acao) */
  footer?: React.ReactNode;
}

/**
 * Modal — Wrapper retrocompativel sobre Dialog (Radix).
 *
 * Traduz a API legada (isOpen/onClose/title/size/footer) para
 * o Dialog compound do shadcn/ui. Todos os consumidores existentes
 * continuam funcionando sem alteracao.
 */
function Modal({
  isOpen,
  onClose,
  children,
  title,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  footer,
}: ModalProps) {
  const { getCssVariable } = useTheme();

  // Cores do tema para o header via variaveis CSS
  const headerBgColor =
    getCssVariable('--brand-primary') ||
    'var(--brand-primary)';

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose],
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay
          className="fixed inset-0 z-[70] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          onEscapeKeyDown={closeOnEsc ? undefined : (e) => e.preventDefault()}
          onPointerDownOutside={
            closeOnOverlayClick ? undefined : (e) => e.preventDefault()
          }
          onInteractOutside={
            closeOnOverlayClick ? undefined : (e) => e.preventDefault()
          }
          className={cn(
            'fixed left-[50%] top-[50%] z-[70] w-full translate-x-[-50%] translate-y-[-50%]',
            'bg-popover text-foreground rounded-lg shadow-xl',
            'border border-border',
            'duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
            sizeClasses[size] || sizeClasses.md,
          )}
        >
          {/* Header com cor do tema */}
          {(title || showCloseButton) && (
            <div
              className="flex items-center justify-between p-4 rounded-t-lg"
              style={{ backgroundColor: headerBgColor }}
            >
              {title && (
                <h3
                  id="modal-title"
                  className="text-lg font-semibold text-white"
                >
                  {title}
                </h3>
              )}
              {showCloseButton && (
                <DialogPrimitive.Close
                  className="text-white/70 hover:text-white transition-colors"
                  aria-label="Fechar"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </DialogPrimitive.Close>
              )}
            </div>
          )}

          {/* Radix exige Title para acessibilidade — esconder visualmente quando wrapper Modal ja tem h3 */}
          <DialogPrimitive.Title className="sr-only">
            {title || 'Dialog'}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {title || 'Dialog content'}
          </DialogPrimitive.Description>

          {/* Body */}
          <div className="p-4">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border bg-muted/50">
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

// ============================================================
// CONFIRM MODAL (wrapper retrocompativel)
// ============================================================

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  loading?: boolean;
}

function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'danger',
  loading = false,
}: ConfirmModalProps) {
  const { t } = useTranslation('buttons');

  const finalConfirmText = confirmText ?? t('confirm');
  const finalCancelText = cancelText ?? t('cancel');

  const confirmButtonClass = {
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    warning: 'bg-yellow-600 hover:bg-yellow-700 text-white',
    info: 'bg-blue-600 hover:bg-blue-700 text-white',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-foreground bg-background border border-border rounded-lg hover:bg-accent"
            disabled={loading}
          >
            {finalCancelText}
          </button>
          <button
            onClick={onConfirm}
            className={cn(
              'px-4 py-2 rounded-lg transition-colors',
              confirmButtonClass[variant],
              loading && 'opacity-50 cursor-not-allowed',
            )}
            disabled={loading}
          >
            {loading ? t('processing') : finalConfirmText}
          </button>
        </>
      }
    >
      <p className="text-muted-foreground">{message}</p>
    </Modal>
  );
}

// ============================================================
// EXPORTS
// ============================================================

export {
  // Compound components (shadcn/ui pattern)
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  // Wrapper retrocompativel
  Modal,
  ConfirmModal,
};

export type { ModalSize, ModalProps, ConfirmModalProps, DialogContentProps };
